import { InitialConfiguration } from '../interfaces';
import {
    dataResponseNotAllowedMessage,
    financialPropsRequired,
    productDurationsPropsRequired,
    serviceReplace,
    trimLineDetailsPropsRequired,
    trimLineUsedDetailsPropsRequired,
    vehicleCatalogsPropsRequired
} from '../Views/CalculatorError/error.constants';
import {
    ComposeArgs,
    MatchDataAlarmBody,
    MatchErrorInterface,
    VehicleDataAlarm
} from '../Views/CalculatorError/error.types';
import { BaseTypes, CarType } from './enums';
import { FinancialProduct, TrimLine, TrimLinesDetail, VehicleModel } from './interfaces';
import { transformText } from './util.class';

const EMPTY = '';
const SPACE = ' ';

/**
 * This class is used for the error-view component, 
 * the purpose of this class is have some utils functions
 * to help that component.
 */
export class ErrorUtilClass {
    private vehicleModels: Array<VehicleModel> = [];

    constructor() {}

    /**
     * This method transform a string value in an array without empty values
     * @param value string.
     * @returns {Array<string>}
     */
    private getWords = (value: string): Array<string> => {
        return value.split(SPACE).filter(word => word !== EMPTY).map(word => word.toLowerCase());
    }

    /**
     * This method compare the different words between two string and if the
     * result is equal to one this going to return true.
     * @param currentValue string.
     * @param item string.
     * @returns {boolean}
     */
    private compareWords = (currentValue: string, item: string): boolean => {
        const cantDifferentFromCurrent = this.getWords(currentValue)
            .filter(word => !this.getWords(item).some(iWord => iWord === word));
        const cantDifferentFromItem = this.getWords(item)
            .filter(word => !this.getWords(currentValue).some(iWord => iWord === word));
        const diffFromCurrentEqs = cantDifferentFromCurrent.length === 1;
        const currentValueLengthVal = this.getWords(currentValue).length > 1;
        const TWO = 2;
        return diffFromCurrentEqs && currentValueLengthVal && cantDifferentFromItem.length <= TWO;
    }

    /**
     * This method filter an array with the difference of the words between the value
     * searched and the current item, the difference defined is one word more or one word less.
     * @param word string.
     * @param search string.
     * @returns {boolean}
     */
    private filterForNumberOfWordsDifferent = (word: string, search: string): boolean => {
        const difference = search.split(SPACE).length - word.split(SPACE).length;
        return difference <= 1 && difference >= -1;
    }

    /**
     * This method return an array with the similar words between a group of words.
     * @param similar Array<string>.
     * @returns {Array<string>}
     */
    private getSimilarOfSimilar = (similar: Array<string>): Array<string> => {
        return similar.reduce((init: Array<string>, word: string, index: number, data: Array<string>) => {
            init = init.concat(word.split(SPACE));

            if (index === data.length - 1) {
                init = init.filter(w => init.filter(nw => nw === w).length === data.length);
                init = [...new Set([...init])];
            }

            return init;
        }, []);
    }

    /**
     * This method filter an array from the number of the different letters between 
     * the searched value and a similar result.
     * @param similar Array<string>.
     * @param searched string.
     * @returns {Array<string>}
     */
    private filterWithLetters = (similar: Array<string>, searched: string): Array<string> => {
        const similarOfSimilar = this.getSimilarOfSimilar(similar);

        return similar.filter(item => {
            const FOUR = 4;
            const wordsOfCurrentValueSearched = searched.split(SPACE);
            const wordsOfSimilarItem = item.split(SPACE);

            const wordsSimilarVSCurrentValue = wordsOfSimilarItem.length >= wordsOfCurrentValueSearched.length;
            const wordsSimilarVSCurrentValuePlusOne = wordsOfSimilarItem.length <= wordsOfCurrentValueSearched.length + 1;
            if (wordsSimilarVSCurrentValue && wordsSimilarVSCurrentValuePlusOne) {
                const withoutEquals = wordsOfSimilarItem.filter(word => !similarOfSimilar.some(w => w === word));
                const wordsOfCurrentValueSearchedWithoutEqualWords = wordsOfCurrentValueSearched
                    .filter(itm => !similarOfSimilar.some(w => w === itm));

                const diff = wordsOfCurrentValueSearchedWithoutEqualWords
                    .toString()
                    .replace(',', EMPTY)
                    .replace('-', '')
                    .split(EMPTY)
                    .reduce((init, curr, index, data) => {

                        withoutEquals.forEach(eqWord => {
                            const letters = eqWord.replace('-', '').split(EMPTY);
                            if (index > letters.length) {
                                init ++;
                            } else {
                                const letter = letters[index];
        
                                if (curr !== letter) {
                                    init ++;
                                }

                                if (index === data.length - 1  && letters.length > data.length) {
                                    init += letters.length - data.length;
                                }
                            }
                        });

                        return init;
                    }, 0);

                return diff <= FOUR;
            } else {
                return true;
            }
        });
    }

    /**
     * This method validate if an object of the kinds defined
     * if different to undefined.
     * @param data VehicleModel | TrimLine | TrimLinesDetail | undefined.
     * @returns boolean.
     */
    private exist = (data: VehicleModel | TrimLine | TrimLinesDetail | undefined): boolean => data !== undefined;

    /**
     * This method find and return a similar data between the initial-data 
     * and the vehicle data.
     * @param param ComposeArgs.
     * @returns SimilarDataProps.
     */
    private getSimilarData = ({ data, find, prop }: ComposeArgs): Array<string> => {
        const currentTransformed = transformText(find);

        const setValidationsAndGetSimilar = (): Array<string> => {
            const matchData = data.filter((item) => 
                new RegExp(currentTransformed, 'i').test(transformText(item[prop].toString())) ||
                new RegExp(transformText(item[prop].toString()), 'i').test(currentTransformed) ||
                this.compareWords(find, item[prop].toString()))
                .map(item => item[prop]).filter(word => this.filterForNumberOfWordsDifferent(word, find));

            return matchData;
        };

        const similar = setValidationsAndGetSimilar();

        if (similar.length > 1) {
            const newSimilar = this.filterWithLetters(similar, find);
            return newSimilar;
        }
        
        return similar;
    }

    /**
     * This method return a model-object and an error-object for a 
     * vehicle-model searched.
     * @param model string.
     * @returns 
     */
    private getModelDataFound = (model: string): {
        modelFound: VehicleModel | undefined, modelData: MatchErrorInterface
    } => {
        const modelFound = this.vehicleModels.find(item =>
            transformText(item.name) ===
            transformText(model));

        return {
            modelFound,
            modelData: new MatchErrorInterface(model, this.exist(modelFound),
                this.exist(modelFound) ? [] :
                    this.getSimilarData({ data: this.vehicleModels, find: model, prop: 'name' }))
                        
        };
    }

    /**
     * This method return a model-object and an error-object for a 
     * vehicle-model searched.
     * @param year string.
     * @param modelFound VehicleModel | undefined.
     * @returns 
     */
    private getYearDataFound = (modelFound: VehicleModel | undefined, year: string): {
        yearFound: TrimLine | undefined, yearData: MatchErrorInterface
    } => {
        const yearFound = modelFound?.trimLines.find(
            (trimLine) => trimLine.modelYear === Number(year));

        return {
            yearFound,
            yearData: new MatchErrorInterface(year, this.exist(yearFound),
                this.exist(yearFound) ? [] :
                    this.getSimilarData
                        ({
                            data: modelFound?.trimLines || [],
                            find: year, prop: 'modelYear'
                        })
            )
        };
    }

    /**
     * This method return a version-object and an error-object for a 
     * model-version searched.
     * @param model string.
     * @param yearFound TrimLine | undefined.
     * @returns 
     */
    private getVersionDataFound = (yearFound: TrimLine | undefined, version: string): {
        versionData: MatchErrorInterface
    } => {
        const versionFound = yearFound?.trimLineDetails.find(
            item =>
                transformText(item.name) ===
                transformText(version)
        );

        return {
            versionData: new MatchErrorInterface(version, this.exist(versionFound),
                this.exist(versionFound) ? [] :
                    this.getSimilarData
                        ({
                            data: yearFound?.trimLineDetails || [],
                            find: version, prop: 'name'
                        })
            )
        };
    }

    /**
     * This method create and return an object when the vehicle-data 
     * throw an error, it object will be send to the alarms-service.
     * @param vehicleModels Array<VehicleModel>.
     * @returns VehicleDataAlarm.
     */
    public getVehicleDataAlarmObject = (
        vehicleModels: Array<VehicleModel>,
        config: InitialConfiguration
    ): MatchDataAlarmBody => {
        this.vehicleModels = vehicleModels;
        const confModel = config.model;
        const confYear = config.year;
        const confVersion = config.version;

        const { modelFound, modelData } = this.getModelDataFound(confModel);
        const { yearFound, yearData } = this.getYearDataFound(modelFound, confYear);
        const { versionData } = this.getVersionDataFound(yearFound, confVersion);

        return { 
            brand: config.brand.toLowerCase(),
            vehicleModel: new VehicleDataAlarm(modelData, yearData, versionData)
         };
    }

    /**
     * This method validate and return an boolean result for the
     * test of the some validations by type of data.
     * @param prop string.
     * @param item FinancialProduct | VehicleModel.
     * @returns {boolean}
     */
    private validateByType = (prop: string, item: FinancialProduct | VehicleModel, type?: CarType): boolean => {
        switch (typeof item[prop]) {
            case BaseTypes.string:
                return item[prop] === null || item[prop] === EMPTY;

            case BaseTypes.number:
                return item[prop] === null || item[prop] === 0;
                    
            case BaseTypes.object:
                if (prop === financialPropsRequired.reverse()[0]) {
                    return item[prop].some(
                        newItem => productDurationsPropsRequired.some(newProp => this.validateByType(newProp, newItem))
                    );
                } else if (prop === vehicleCatalogsPropsRequired[0]) {
                    return item[prop].some(
                        newItem => Object.keys(newItem).some(newProp => this.validateByType(newProp, newItem, type))
                    );
                } else if (prop === vehicleCatalogsPropsRequired[1]) {
                    const propsRequired = type === CarType.USED
                        ? trimLineUsedDetailsPropsRequired
                        : trimLineDetailsPropsRequired;
                    return item[prop].some(
                        newItem => propsRequired.some(newProp => this.validateByType(newProp, newItem))
                    );
                }

                return item[prop] === null;
        
            default:
                return item[prop] === undefined;
        }
    }

    /**
     * This method validate if the data of the products is right
     * else it throw an error.
     * @param products Array<FinancialProduct>.
     * @returns {Array<FinancialProduct>}
     */
    public validateFinancialProductsData = (products: Array<FinancialProduct>): Array<FinancialProduct> => {
        try {
            products.forEach(item => {
                const invalid = financialPropsRequired.some(prop => this.validateByType(prop, item));

                if (invalid) throw new Error(dataResponseNotAllowedMessage.replace(serviceReplace, 'financial-products'));
            });
        } catch (error) {
            throw new Error(error.message);
        }

        return products;
    }

    /**
     * This method validate if the data of the vehicles-catalog is right
     * else it throw an error.
     * @param vehicles Array<VehicleModel>.
     * @returns {Array<VehicleModel>}
     */
    public validateVehiclesCatalogData = (vehicles: Array<VehicleModel>, type?: CarType): Array<VehicleModel> => {
       try {
            vehicles.forEach(item => {
               const invalid = Object.keys(item).some(prop => this.validateByType(prop, item, type));               

               if (invalid) throw new Error(dataResponseNotAllowedMessage.replace(serviceReplace, 'vehicles-models'));
           });
       } catch (error) {
           throw new Error(error.message);
       }

       return vehicles;
   }
}
